import { useState, useEffect, useCallback } from "react";
import DashboardLayout from "../layouts/DashboardLayout";
import api from "../api";
import { toast } from "react-toastify";
import { Bug, Search, RefreshCw, ChevronLeft, ChevronRight, X, AlertTriangle, User, Tag, Clock, Server, Hash } from "lucide-react";

const PAGE_SIZE = 50;

function ErrorDetailModal({ log, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const formatDate = (iso) => {
    if (!iso) return "—";
    const utcStr = iso.endsWith('Z') ? iso : `${iso}Z`;
    return new Date(utcStr).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  };

  const Field = ({ icon, label, value, mono = false }) => (
    <div className="space-y-1">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-secondary-400 uppercase tracking-wide">
        {icon}
        {label}
      </div>
      <div className={`text-sm text-secondary-800 ${mono ? "font-mono" : ""}`}>
        {value ?? <span className="text-secondary-300 italic">—</span>}
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-xl bg-rose-100 flex items-center justify-center">
              <Bug size={16} className="text-rose-500" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-secondary-800">Error Detail</h2>
              <p className="text-xs text-secondary-400 font-mono">#{log.id}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-secondary-400 hover:bg-secondary-100 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Modal body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          <div className="grid grid-cols-2 gap-5">
            <Field icon={<Tag size={11} />} label="Tag" value={
              <span className="inline-block px-2 py-0.5 rounded-lg bg-rose-100 text-rose-600 text-xs font-medium">
                {log.tag}
              </span>
            } />
            <Field icon={<Clock size={11} />} label="Time" value={formatDate(log.created_at)} />
            <Field icon={<User size={11} />} label="User ID" value={log.user_id} mono />
            <Field icon={<Server size={11} />} label="Source" value={log.source} />
            <Field icon={<Hash size={11} />} label="Org ID" value={log.org_uid} mono />
          </div>

          <div className="space-y-1">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-secondary-400 uppercase tracking-wide">
              <Hash size={11} />
              Message
            </div>
            <p className="text-sm text-secondary-800 leading-relaxed">{log.message}</p>
          </div>

          {log.detail && (
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-400 uppercase tracking-wide">
                <Bug size={11} />
                Detail / Stack Trace
              </div>
              <pre className="text-xs text-secondary-700 whitespace-pre-wrap break-all font-mono bg-secondary-50 rounded-xl p-4 border border-secondary-100 max-h-72 overflow-y-auto leading-relaxed">
                {log.detail}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ErrorLogs() {
  const [logs, setLogs] = useState([]);
  const [tags, setTags] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedLog, setSelectedLog] = useState(null);

  const [filters, setFilters] = useState({
    page: 1,
    limit: PAGE_SIZE,
    search: "",
    tag: "",
    org_uid: "",
    date_from: "",
    date_to: "",
  });

  const fetchLogs = useCallback(async (activeFilters) => {
    setLoading(true);
    try {
      const params = {};
      if (activeFilters.page) params.page = activeFilters.page;
      if (activeFilters.limit) params.limit = activeFilters.limit;
      if (activeFilters.search) params.search = activeFilters.search;
      if (activeFilters.tag) params.tag = activeFilters.tag;
      if (activeFilters.org_uid) params.org_uid = activeFilters.org_uid;
      if (activeFilters.date_from) params.date_from = activeFilters.date_from;
      if (activeFilters.date_to) params.date_to = activeFilters.date_to;

      const res = await api.getErrorLogs(params);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.pages || 1);
    } catch (err) {
      if (err.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error("Failed to load error logs");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTags = useCallback(async () => {
    try {
      const res = await api.getErrorLogTags();
      setTags(res.data.tags || []);
    } catch {
      // non-critical
    }
  }, []);

  const fetchOrgs = useCallback(async () => {
    try {
      const res = await api.getErrorLogOrgs();
      setOrgs(res.data.orgs || []);
    } catch {
      // non-critical
    }
  }, []);

  useEffect(() => {
    fetchLogs(filters);
    fetchTags();
    fetchOrgs();
  }, []);

  const applyFilters = () => {
    const updated = { ...filters, page: 1 };
    setFilters(updated);
    fetchLogs(updated);
  };

  const clearFilters = () => {
    const reset = { page: 1, limit: PAGE_SIZE, search: "", tag: "", org_uid: "", date_from: "", date_to: "" };
    setFilters(reset);
    fetchLogs(reset);
  };

  const goToPage = (page) => {
    const updated = { ...filters, page };
    setFilters(updated);
    fetchLogs(updated);
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    const utcStr = iso.endsWith('Z') ? iso : `${iso}Z`;
    return new Date(utcStr).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  };

  if (accessDenied) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-96 gap-4 text-center">
          <AlertTriangle size={48} className="text-rose-400" />
          <h2 className="text-xl font-bold text-secondary-800">Access Restricted</h2>
          <p className="text-secondary-500 max-w-md">
            Error logs are only accessible to users belonging to the admin organisation.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-rose-100 flex items-center justify-center">
              <Bug size={20} className="text-rose-500" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-secondary-800">Error Logs</h1>
              <p className="text-xs text-secondary-400">{total.toLocaleString()} total entries · 50 per page · newest first</p>
            </div>
          </div>
          <button
            onClick={() => fetchLogs(filters)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-secondary-200 text-secondary-600 hover:bg-secondary-50 text-sm transition-colors"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white/70 backdrop-blur-sm border border-white/20 shadow-soft rounded-2xl p-4">
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-48">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" />
              <input
                type="text"
                placeholder="Search message or detail..."
                value={filters.search}
                onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && applyFilters()}
                className="w-full pl-9 pr-3 py-2 rounded-xl border border-secondary-200 bg-white text-sm text-secondary-700 placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>

            <select
              value={filters.tag}
              onChange={(e) => setFilters((f) => ({ ...f, tag: e.target.value }))}
              className="px-3 py-2 rounded-xl border border-secondary-200 bg-white text-sm text-secondary-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
            >
              <option value="">All Tags</option>
              {tags.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>

            <select
              value={filters.org_uid}
              onChange={(e) => setFilters((f) => ({ ...f, org_uid: e.target.value }))}
              className="px-3 py-2 rounded-xl border border-secondary-200 bg-white text-sm text-secondary-700 focus:outline-none focus:ring-2 focus:ring-primary-300 max-w-[200px]"
            >
              <option value="">All Orgs</option>
              {orgs.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>

            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
              className="px-3 py-2 rounded-xl border border-secondary-200 bg-white text-sm text-secondary-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
              title="From date"
            />
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
              className="px-3 py-2 rounded-xl border border-secondary-200 bg-white text-sm text-secondary-700 focus:outline-none focus:ring-2 focus:ring-primary-300"
              title="To date"
            />

            <button
              onClick={applyFilters}
              className="px-4 py-2 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              Apply
            </button>
            <button
              onClick={clearFilters}
              className="px-3 py-2 rounded-xl border border-secondary-200 text-secondary-500 hover:bg-secondary-50 text-sm transition-colors"
              title="Clear filters"
            >
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white/70 backdrop-blur-sm border border-white/20 shadow-soft rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-8 space-y-3">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-10 bg-secondary-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-secondary-400">
              <Bug size={36} />
              <p className="text-sm">No error logs found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-secondary-100 bg-secondary-50/50">
                    <th className="px-4 py-3 text-left font-semibold text-secondary-600 w-16">#</th>
                    <th className="px-4 py-3 text-left font-semibold text-secondary-600 w-40">Time</th>
                    <th className="px-4 py-3 text-left font-semibold text-secondary-600 w-36">Tag</th>
                    <th className="px-4 py-3 text-left font-semibold text-secondary-600">Message</th>
                    <th className="px-4 py-3 text-left font-semibold text-secondary-600 w-24">User ID</th>
                    <th className="px-4 py-3 text-left font-semibold text-secondary-600 w-64">Org ID</th>
                    <th className="px-4 py-3 text-left font-semibold text-secondary-600 w-24">Source</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr
                      key={log.id}
                      onClick={() => setSelectedLog(log)}
                      className="border-b border-secondary-50 hover:bg-rose-50/40 cursor-pointer transition-colors"
                    >
                      <td className="px-4 py-3 text-secondary-400 font-mono text-xs">{log.id}</td>
                      <td className="px-4 py-3 text-secondary-500 text-xs whitespace-nowrap">{formatDate(log.created_at)}</td>
                      <td className="px-4 py-3">
                        <span className="inline-block px-2 py-0.5 rounded-lg bg-rose-100 text-rose-600 text-xs font-medium">
                          {log.tag}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-secondary-700 max-w-xl truncate">{log.message}</td>
                      <td className="px-4 py-3 text-secondary-500 text-xs font-mono">{log.user_id ?? "—"}</td>
                      <td className="px-4 py-3 text-secondary-500 text-xs font-mono truncate max-w-[256px]">{log.org_uid ?? "—"}</td>
                      <td className="px-4 py-3 text-secondary-400 text-xs">{log.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between text-sm text-secondary-500">
            <span>
              Page {filters.page} of {totalPages} &nbsp;·&nbsp; {total.toLocaleString()} entries
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => goToPage(filters.page - 1)}
                disabled={filters.page <= 1}
                className="p-2 rounded-xl border border-secondary-200 hover:bg-secondary-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={14} />
              </button>
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                const pg = Math.max(1, Math.min(totalPages - 4, filters.page - 2)) + i;
                return (
                  <button
                    key={pg}
                    onClick={() => goToPage(pg)}
                    className={`w-8 h-8 rounded-xl text-xs font-medium transition-colors ${
                      pg === filters.page
                        ? "bg-primary-500 text-white"
                        : "border border-secondary-200 hover:bg-secondary-50"
                    }`}
                  >
                    {pg}
                  </button>
                );
              })}
              <button
                onClick={() => goToPage(filters.page + 1)}
                disabled={filters.page >= totalPages}
                className="p-2 rounded-xl border border-secondary-200 hover:bg-secondary-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail modal */}
      {selectedLog && (
        <ErrorDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
      )}
    </DashboardLayout>
  );
}
